import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchArticles, getPlaceholderImage } from '../utils/articleApi';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AllArticles = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [categories, setCategories] = useState(['All']);

  const loadArticles = async (page = 1, category = selectedCategory, search = searchTerm) => {
    setLoading(true);
    setError('');
    
    try {
      const result = await fetchArticles({
        page,
        limit: 12,
        category: category !== 'All' ? category : undefined,
        search: search || undefined
      });
      
      setArticles(result.articles);
      setPagination(result.pagination);
      
      if (categories.length === 1 && result.articles.length > 0) {
        const uniqueCategories = ['All', ...new Set(result.articles.map(a => a.category))];
        setCategories(uniqueCategories);
      }
    } catch (err) {
      setError(err.message || 'Failed to load articles. Please try again later.');
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArticles();
  }, []);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      loadArticles(1, selectedCategory, searchTerm);
    }, 500);
    
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, selectedCategory]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      loadArticles(newPage, selectedCategory, searchTerm);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const SkeletonCard = () => (
    <div style={styles.card}>
      <div style={{ ...styles.imageWrapper, background: '#E0E4E8' }}></div>
      <div style={styles.cardContent}>
        <div style={{ ...styles.meta, marginBottom: '12px' }}>
          <div style={{ width: '100px', height: '14px', background: '#E0E4E8', borderRadius: '4px' }}></div>
          <div style={{ width: '60px', height: '14px', background: '#E0E4E8', borderRadius: '4px' }}></div>
        </div>
        <div style={{ width: '100%', height: '20px', background: '#E0E4E8', borderRadius: '4px', marginBottom: '12px' }}></div>
        <div style={{ width: '100%', height: '14px', background: '#E0E4E8', borderRadius: '4px', marginBottom: '8px' }}></div>
        <div style={{ width: '80%', height: '14px', background: '#E0E4E8', borderRadius: '4px', marginBottom: '20px' }}></div>
        <div style={{ display: 'flex', gap: '12px', paddingTop: '16px', borderTop: '1px solid #E0E4E8' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#E0E4E8' }}></div>
          <div style={{ flex: 1 }}>
            <div style={{ width: '120px', height: '14px', background: '#E0E4E8', borderRadius: '4px', marginBottom: '8px' }}></div>
            <div style={{ width: '80px', height: '12px', background: '#E0E4E8', borderRadius: '4px' }}></div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={styles.page}>
      <Navbar />
      
      <section style={styles.banner}>
        <div style={styles.container}>
          <div style={styles.bannerContent}>
            <h1 style={styles.bannerTitle}>Health & Wellness Resources</h1>
            <p style={styles.bannerSubtitle}>
              Expert medical advice and health information from our team of healthcare professionals
            </p>
          </div>
        </div>
      </section>

      <section style={styles.searchSection}>
        <div style={styles.container}>
          <div style={styles.searchWrapper}>
            <div style={styles.searchBox}>
              <Search size={20} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
            </div>
            
            <div style={styles.filterWrapper}>
              <Filter size={18} style={styles.filterIcon} />
              <span style={styles.filterLabel}>Filter by:</span>
              <div style={styles.categoryButtons}>
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    style={{
                      ...styles.categoryBtn,
                      ...(selectedCategory === category ? styles.categoryBtnActive : {})
                    }}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={styles.articlesSection}>
        <div style={styles.container}>
          {error && (
            <div style={styles.errorContainer}>
              <p style={styles.errorText}>{error}</p>
            </div>
          )}

          {!error && (
            <>
              <div style={styles.resultsInfo}>
                <h2 style={styles.resultsTitle}>
                  {loading ? 'Loading...' : `${pagination.total} ${pagination.total === 1 ? 'Article' : 'Articles'} Found`}
                </h2>
              </div>

              {loading ? (
                <div style={styles.grid}>
                  {[...Array(6)].map((_, i) => (
                    <SkeletonCard key={i} />
                  ))}
                </div>
              ) : (
                <>
                  <div style={styles.grid}>
                    {articles.map((post) => (
                      <article
                        key={post.id}
                        style={styles.card}
                        className="hover-lift"
                        onClick={() => navigate(`/article/${post.slug}`)}
                        onMouseEnter={(e) => {
                          const img = e.currentTarget.querySelector('img');
                          if (img) img.style.transform = 'scale(1.05)';
                          e.currentTarget.style.boxShadow = '0 8px 24px rgba(26, 115, 232, 0.12)';
                        }}
                        onMouseLeave={(e) => {
                          const img = e.currentTarget.querySelector('img');
                          if (img) img.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div style={styles.imageWrapper}>
                          <img
                            src={post.image || getPlaceholderImage(400, 220)}
                            alt={post.title}
                            style={styles.image}
                            onError={(e) => {
                              const placeholder = getPlaceholderImage(400, 220);
                              if (e.target.src !== placeholder) {
                                e.target.src = placeholder;
                              }
                            }}
                          />
                          <div style={styles.categoryBadge}>{post.category}</div>
                        </div>
                        
                        <div style={styles.cardContent}>
                          <div style={styles.meta}>
                            <span style={styles.date}>
                              {new Date(post.date).toLocaleDateString('en-US', { 
                                month: 'long', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })}
                            </span>
                            <span style={styles.readTime}>
                              <Clock size={14} style={{ marginRight: '4px' }} />
                              {post.readTime}
                            </span>
                          </div>
                          
                          <h3 style={styles.cardTitle}>{post.title}</h3>
                          <p style={styles.excerpt}>{post.excerpt}</p>
                          
                          <div style={styles.authorInfo}>
                            <div style={styles.authorAvatar}>
                              {post.author.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div>
                              <div style={styles.authorName}>{post.author}</div>
                              <div style={styles.authorTitle}>{post.authorTitle}</div>
                            </div>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>

                  {articles.length === 0 && !loading && (
                    <div style={styles.noResults}>
                      <p style={styles.noResultsText}>No articles found matching your search.</p>
                    </div>
                  )}

                  {pagination.pages > 1 && (
                    <div style={styles.pagination}>
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        style={{
                          ...styles.paginationBtn,
                          ...(pagination.page === 1 ? styles.paginationBtnDisabled : {})
                        }}
                      >
                        <ChevronLeft size={18} />
                        Previous
                      </button>
                      
                      <div style={styles.paginationInfo}>
                        Page {pagination.page} of {pagination.pages}
                      </div>
                      
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.pages}
                        style={{
                          ...styles.paginationBtn,
                          ...(pagination.page === pagination.pages ? styles.paginationBtnDisabled : {})
                        }}
                      >
                        Next
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#FFFFFF'
  },
  banner: {
    background: 'linear-gradient(135deg, #286CFF 0%, #4A85FF 50%, #133E97 100%)',
    padding: '120px 0 80px',
    marginTop: '70px'
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px'
  },
  bannerContent: {
    textAlign: 'center',
    color: 'white'
  },
  bannerTitle: {
    fontSize: '48px',
    fontWeight: '700',
    lineHeight: '1.2',
    marginBottom: '16px'
  },
  bannerSubtitle: {
    fontSize: '18px',
    lineHeight: '1.6',
    maxWidth: '700px',
    margin: '0 auto',
    opacity: 0.95
  },
  searchSection: {
    padding: '40px 0',
    background: 'white',
    borderBottom: '1px solid #E0E4E8'
  },
  searchWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  searchBox: {
    position: 'relative',
    maxWidth: '600px',
    width: '100%'
  },
  searchIcon: {
    position: 'absolute',
    left: '20px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#8F9BA8'
  },
  searchInput: {
    width: '100%',
    padding: '16px 20px 16px 52px',
    border: '2px solid #D1D6DB',
    borderRadius: '12px',
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.2s ease'
  },
  filterWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap'
  },
  filterIcon: {
    color: '#8F9BA8'
  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333333'
  },
  categoryButtons: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap'
  },
  categoryBtn: {
    background: 'white',
    color: '#8F9BA8',
    border: '1px solid #D1D6DB',
    borderRadius: '20px',
    padding: '8px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  categoryBtnActive: {
    background: '#286CFF',
    color: 'white',
    borderColor: '#286CFF'
  },
  articlesSection: {
    padding: '64px 0',
    background: '#F4F6F8'
  },
  resultsInfo: {
    marginBottom: '32px'
  },
  resultsTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#333333'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '32px'
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    overflow: 'hidden',
    border: '1px solid #E0E4E8',
    transition: 'all 0.2s ease',
    cursor: 'pointer'
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    height: '220px',
    overflow: 'hidden',
    background: '#F4F6F8'
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
    display: 'block'
  },
  categoryBadge: {
    position: 'absolute',
    top: '16px',
    left: '16px',
    background: '#FFD28A',
    color: '#4A4A4A',
    padding: '6px 16px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '700'
  },
  cardContent: {
    padding: '24px'
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
    fontSize: '14px',
    color: '#8F9BA8'
  },
  date: {
    fontSize: '14px'
  },
  readTime: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px'
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#333333',
    marginBottom: '12px',
    lineHeight: '1.3'
  },
  excerpt: {
    fontSize: '14px',
    color: '#8F9BA8',
    lineHeight: '1.5',
    marginBottom: '20px'
  },
  authorInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    paddingTop: '16px',
    borderTop: '1px solid #E0E4E8'
  },
  authorAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: '#286CFF',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600'
  },
  authorName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333333'
  },
  authorTitle: {
    fontSize: '12px',
    color: '#8F9BA8'
  },
  noResults: {
    textAlign: 'center',
    padding: '64px 24px'
  },
  noResultsText: {
    fontSize: '18px',
    color: '#8F9BA8'
  },
  errorContainer: {
    background: '#FEE2E2',
    border: '1px solid #FCA5A5',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '32px',
    textAlign: 'center'
  },
  errorText: {
    fontSize: '16px',
    color: '#DC2626',
    margin: 0
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '24px',
    marginTop: '48px',
    padding: '24px 0'
  },
  paginationBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#286CFF',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  paginationBtnDisabled: {
    background: '#E0E4E8',
    color: '#8F9BA8',
    cursor: 'not-allowed'
  },
  paginationInfo: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333333'
  }
};

export default AllArticles;
